import { InventoryHoldReasonCode } from "~/redux/public/inventory.openApi";
import { PutAwayTaskSummaryDto } from "~/types/api";

import {
  CANT_INDUCT_REASON_CODES,
  CANT_INDUCT_REASON_TRANSLATION_KEY_PREFIX,
  CantInductAdditionalDetail,
  CantInductReasonsMap,
  CantInductReasonTranslationKey,
  INVENTORY_HOLD_REASON_CODES,
  KnownCantInductReasonCode,
  PutawayTaskCantInductReasons
} from "./autostorePutaway.types";

function isKnownCantInductReasonCode(
  reasonCode: string
): reasonCode is KnownCantInductReasonCode {
  return (CANT_INDUCT_REASON_CODES as readonly string[]).includes(reasonCode);
}

function isInventoryHoldReasonCode(
  detailCode: string
): detailCode is InventoryHoldReasonCode {
  return (INVENTORY_HOLD_REASON_CODES as readonly string[]).includes(
    detailCode
  );
}

function mapAdditionalDetail(
  detail: string
): CantInductAdditionalDetail | null {
  const trimmedDetail = detail.trim();
  if (!trimmedDetail) {
    return null;
  }

  const normalizedDetail = trimmedDetail.toLowerCase();
  return {
    original: trimmedDetail,
    ...(isInventoryHoldReasonCode(normalizedDetail)
      ? { translationKey: normalizedDetail }
      : {})
  };
}

/**
 * Maps `cantInductReasons` on a putaway task for `CantInductAlert` and ineligibility.
 *
 * Reason codes are keyed by the normalized backend string (trimmed, lowercased).
 * Each entry keeps the trimmed `originalReasonCode` for display. Known codes use
 * prefixed `english.json` keys for translation; unknown codes still block
 * induction and display as the trimmed original string.
 *
 * Additional details keep the trimmed original string. Known hold reason codes
 * also get a `translationKey` for localized display.
 */
export function getCantInductReasonsMap(
  putawayTask?: PutAwayTaskSummaryDto | null
): CantInductReasonsMap {
  return (putawayTask?.cantInductReasons ?? []).reduce<CantInductReasonsMap>(
    (acc, reason) => {
      const originalReasonCode = reason.reasonCode?.trim();
      if (!originalReasonCode) {
        return acc;
      }

      const reasonCode = originalReasonCode.toLowerCase();
      const translationReasonKey = isKnownCantInductReasonCode(reasonCode)
        ? (`${CANT_INDUCT_REASON_TRANSLATION_KEY_PREFIX}${reasonCode}` satisfies CantInductReasonTranslationKey)
        : undefined;
      const additionalDetails = (reason.additionalDetails ?? [])
        .map(mapAdditionalDetail)
        .filter(
          (detail): detail is CantInductAdditionalDetail => detail !== null
        );

      return {
        ...acc,
        [reasonCode]: {
          originalReasonCode,
          additionalDetails,
          translationReasonKey
        }
      };
    },
    {}
  );
}

export function getPutawayTaskCantInductReasons(
  putawayTask?: PutAwayTaskSummaryDto | null
): PutawayTaskCantInductReasons {
  const cantInductReasonsMap = getCantInductReasonsMap(putawayTask);

  return {
    cantInductReasonsMap,
    isPutawayTaskIneligible: Object.keys(cantInductReasonsMap).length > 0
  };
}
